import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

import { stompService } from 'services/socketService';
import { downloadAssetsFile, startDownloadAssets } from '../functions/download';
import { startUploadAssets } from '../functions/Upload';

/* ----------------------------------
   DOWNLOAD PROGRESS CONFIG (GLOBAL)
---------------------------------- */
const createTempId = () => `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const DOWNLOAD_PROGRESS = {
  PREPARING: {
    start: 0,
    end: 10,
  },
  PROCESSING: {
    start: 10,
    end: 20,
  },
  DOWNLOADING: {
    start: 20,
    end: 99,
  },
  DONE: {
    start: 99,
    end: 100,
  },
};

export const UPLOAD_PROGRESS = {
  PREPARING: { start: 0, end: 10 },
  UPLOADING: { start: 10, end: 85 },
  PROCESSING: { start: 85, end: 95 },
  DONE: { start: 95, end: 100 },
};
const DownloadAssetsContext = createContext(null);

/* ----------------------------------
   GENERIC PROGRESS MAPPER
---------------------------------- */

const mapProgress = (phase, percent = 0, states = DOWNLOAD_PROGRESS) => {
  const cfg = states[phase];
  if (!cfg) return 0;

  if (cfg.start === cfg.end) return cfg.end;

  return Math.min(cfg.end, cfg.start + Math.floor(((cfg.end - cfg.start) * percent) / 100));
};

export const DownloadAssetsProvider = ({ children }) => {
  const [downloadingAssets, setDownloadingAssets] = useState([]);
  const [globalDownloadProgress, setGlobalDownloadProgress] = useState(0);
  const [refreshRef, setrefreshRef] = useState(0);

  const uploadListeners = useRef([]);
  const subscribedRef = useRef(false);
  const uploadIdMapRef = useRef({});

  /* ----------------------------------
     SOCKET SUBSCRIPTIONS
  ---------------------------------- */

  useEffect(() => {
    if (!stompService.isConnected()) return;
    const unsubscribe = stompService.subscribe('/user/topic/media.dam.final', (data) => {
      uploadListeners.current.forEach((cb) =>
        cb({ ...data, activityId: data.activityId || data.uniqueId }),
      );

      handleDownloadSocket(data);
      handleUploadSocket(data);
    });

    subscribedRef.current = true;

    return () => {
      unsubscribe?.();
      subscribedRef.current = false;
    };
  }, [stompService.isConnected]);

  /* ----------------------------------
     SOCKET STATUS HANDLER
  ---------------------------------- */
  const handleDownloadSocket = async (data) => {
    const { uniqueId, status } = data;
    if (!uniqueId) return;

    /* ---------- PREPARING ---------- */
    if (status === 'PREPARING') {
      updateStatus(uniqueId, 'PREPARING', mapProgress('PREPARING', 100));
      return;
    }

    /* ---------- PROCESSING ---------- */
    if (status === 'PROCESSING') {
      updateStatus(uniqueId, 'PROCESSING', mapProgress('PROCESSING', 100));
      return;
    }
    if (status === 'FAILED') {
      updateStatus(uniqueId, 0, mapProgress('PROCESSING', 100));
      return;
    }

    /* ---------- READY → DOWNLOADING ---------- */
    if (status === 'READY') {
      updateStatus(uniqueId, 'DOWNLOADING', mapProgress('DOWNLOADING', 0));

      notify(uniqueId, 'DOWNLOADING', mapProgress('DOWNLOADING', 0), data);

      try {
        await downloadAssetsFile({ downloadId: uniqueId }, (downloadPercent) => {
          const progress = mapProgress('DOWNLOADING', downloadPercent);

          updateStatus(uniqueId, 'DOWNLOADING', progress);
          notify(uniqueId, 'DOWNLOADING', progress, data);
        });

        updateStatus(uniqueId, 'DONE', DOWNLOAD_PROGRESS.DONE.end);

        notify(uniqueId, 2, DOWNLOAD_PROGRESS.DONE.end, data);

        cleanup(uniqueId);
      } catch {
        updateStatus(uniqueId, 'FAILED', 0);
        notify(uniqueId, 'FAILED', 0, data);
        cleanup(uniqueId);
      }

      return;
    }

    /* ---------- FAILED ---------- */
    if (status === 'FAILED') {
      updateStatus(uniqueId, 'FAILED', 0);
      notify(uniqueId, 'FAILED', 0, data);
      cleanup(uniqueId);
    }
  };

  /* ----------------------------------
     START DOWNLOAD
  ---------------------------------- */
  const startDownload = async (payload) => {
    const job = await startDownloadAssets(payload);
    notify(job.downloadId, 'PREPARING', 0, {});
    setDownloadingAssets((prev) => [
      ...prev,
      {
        id: job.downloadId,
        payload,
        fileName: job.fileName,
        status: 'PREPARING',
        progress: mapProgress('PREPARING', 0),
        showProgress: true,
      },
    ]);
  };

  /* ----------------------------------
     STATE HELPERS
  ---------------------------------- */
  const updateStatus = (id, status, progress = 0) => {
    setDownloadingAssets((prev) => {
      const updated = prev.map((d) =>
        d.id === id ? { ...d, status, progress, showProgress: true } : d,
      );

      const active = updated.find((d) => d.status === 'DOWNLOADING');

      setGlobalDownloadProgress(active ? active.progress : 0);

      return updated;
    });
  };

  const cleanup = (id) => {
    setDownloadingAssets((prev) => {
      const updated = prev.filter((d) => d.id !== id);

      const active = updated.find((d) => d.status === 'DOWNLOADING');

      setGlobalDownloadProgress(active ? active.progress : 0);

      return updated;
    });
  };

  /* ----------------------------------
     LISTENER NOTIFIER
  ---------------------------------- */
  const notify = (id, status, progress, data) => {
    uploadListeners.current.forEach((cb) =>
      cb({
        ...data,
        activityId: data.activityId || id,
        status,
        progress,
        showProgress: true,
      }),
    );
  };

  /* ----------------------------------
     LISTENER API
  ---------------------------------- */
  const registerUploadListener = (cb) => {
    uploadListeners.current.push(cb);
    return () => {
      uploadListeners.current = uploadListeners.current.filter((fn) => fn !== cb);
    };
  };

  /* ----------------------------------
     UPLOAD START (OPTIMISTIC)
  ---------------------------------- */

  const UploadFiles = async (formData) => {
    const tempId = createTempId();

    // 1️⃣ Immediate optimistic UI
    notify(tempId, 'PREPARING', 0, {
      uploadId: tempId,
      isTemp: true,
    });

    try {
      const job = await startUploadAssets(formData, (percent) => {
        const percentMapped = mapProgress('UPLOADING', percent, UPLOAD_PROGRESS);
        notify(tempId, 'UPLOADING', percentMapped, { uploadId: tempId });
      });
      // 🔥 SAVE ID MAPPING
      uploadIdMapRef.current[job.uploadId] = tempId;

      // // 2️⃣ Inform UI about ID swap
      notify(tempId, 'SWAP', 0, {
        action: 'SWAP',
        activityId: tempId,
        oldId: tempId,
        newId: job.uploadId,
      });

      return job;
    } catch (err) {
      notify(tempId, 'FAILED', 0, { uploadId: tempId });
      throw err;
    }
  };

  const handleUploadSocket = (data) => {
    const { activityId: requestId, status, percent = 0 } = data;

    data = {
      ...data,
      showProgress: true,
      progress: mapProgress(status, percent),
    };
    if (!requestId) return;
    // 🔥 MAP realId → tempId
    const mappedId = requestId;

    if (status === 1) {
      notify(mappedId, 'PROCESSING', mapProgress('PROCESSING', 100, UPLOAD_PROGRESS), data);
      return;
    }

    if (status === 2) {
      notify(mappedId, 2, UPLOAD_PROGRESS.DONE.end, data, UPLOAD_PROGRESS);
      delete uploadIdMapRef.current[requestId];
      setrefreshRef(refreshRef + 1);
      return;
    }

    if (status === 'FAILED') {
      notify(mappedId, 'FAILED', 0, data, UPLOAD_PROGRESS);
      delete uploadIdMapRef.current[requestId];
    }
  };

  return (
    <DownloadAssetsContext.Provider
      value={{
        startDownload,
        downloadingAssets,
        registerUploadListener,
        globalDownloadProgress,
        DOWNLOAD_PROGRESS,
        UploadFiles,
        UPLOAD_PROGRESS,
        refreshRef,
      }}
    >
      {children}
    </DownloadAssetsContext.Provider>
  );
};

export const useDownloadAssets = () => {
  const ctx = useContext(DownloadAssetsContext);
  if (!ctx) {
    throw new Error('useDownloadAssets must be used inside DownloadAssetsProvider');
  }
  return ctx;
};
